import { useState, useEffect, useCallback, useRef } from "react";
import { Routes, Route, Link, useNavigate, useParams } from "react-router-dom";
import MainLayout from "@/components/layout/MainLayout";
import { Card, CardContent, CardHeader, CardTitle, CardDescription, CardFooter } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { NativeSelect } from "@/components/ui/native-select";
import { MultiSelect } from "@/components/ui/multi-select";
import { SearchablePicker } from "@/components/ui/searchable-picker";
import { Separator } from "@/components/ui/separator";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription,
} from "@/components/ui/dialog";
import { API, useAuth } from "@/App";
import axios from "axios";
import { toast } from "sonner";
import { 
  Plus, Search, ArrowLeft, MapPin, Clock, CheckCircle2, Send, 
  Phone, Mail, User, Edit, Trash2, Eye, Camera, FileText,
  AlertTriangle, Calendar, Play, Pause, Flag, PenTool,
  Download, Share2, Printer, MessageCircle, History,
  Building, Wrench, Zap, Droplet, PaintBucket, Users, X
} from "lucide-react";
import { extractErrorMessage } from "@/lib/errorMessage";

// Configurações
const categorias = [
  { value: "eletrica", label: "Elétrica", icon: Zap },
  { value: "hidraulica", label: "Hidráulica", icon: Droplet },
  { value: "civil", label: "Civil", icon: Building },
  { value: "pintura", label: "Pintura", icon: PaintBucket },
  { value: "manutencao_predial", label: "Manutenção Predial", icon: Wrench },
  { value: "serralheria", label: "Serralheria", icon: Wrench },
  { value: "limpeza", label: "Limpeza", icon: Wrench },
  { value: "outro", label: "Outro", icon: Wrench }
];

const tipos = [
  { value: "manutencao", label: "Manutenção" },
  { value: "reparo", label: "Reparo" }
];

const prioridades = [
  { value: "baixa", label: "Baixa", class: "bg-slate-100 text-slate-700" },
  { value: "media", label: "Média", class: "bg-blue-100 text-blue-700" },
  { value: "alta", label: "Alta", class: "bg-amber-100 text-amber-700" },
  { value: "urgente", label: "Urgente", class: "bg-red-100 text-red-700" }
];

const statusConfig = {
  encaminhado: { label: "Encaminhado para execução", class: "bg-blue-100 text-blue-700", icon: Send },
  em_execucao: { label: "Em execução", class: "bg-amber-100 text-amber-700", icon: Play },
  paralisado: { label: "Paralisado", class: "bg-slate-200 text-slate-700", icon: Pause },
  atrasado: { label: "Atrasado", class: "bg-red-100 text-red-700", icon: AlertTriangle },
  pendencia: { label: "Pendência", class: "bg-orange-100 text-orange-700", icon: Flag },
  concluido: { label: "Concluído", class: "bg-emerald-100 text-emerald-700", icon: CheckCircle2 },
  aditivado: { label: "Aditivado", class: "bg-purple-100 text-purple-700", icon: FileText },
  // backwards compat for old data
  enviado: { label: "Enviado", class: "bg-blue-100 text-blue-700", icon: Send },
  no_prazo: { label: "No Prazo", class: "bg-green-100 text-green-700", icon: Clock },
  com_pendencias: { label: "Com Pendências", class: "bg-orange-100 text-orange-700", icon: Flag }
};

// Only the 7 active status options for filters and updates
const activeStatusOptions = [
  { value: "encaminhado", label: "Encaminhado para execução" },
  { value: "em_execucao", label: "Em execução" },
  { value: "paralisado", label: "Paralisado" },
  { value: "atrasado", label: "Atrasado" },
  { value: "pendencia", label: "Pendência" },
  { value: "concluido", label: "Concluído" },
  { value: "aditivado", label: "Aditivado serviços ao orçamento" }
];

function formatDate(dateStr) {
  if (!dateStr) return "-";
  return new Date(dateStr).toLocaleDateString('pt-BR');
}

function formatDateTime(dateStr) {
  if (!dateStr) return "-";
  return new Date(dateStr).toLocaleString('pt-BR', { 
    day: '2-digit', month: '2-digit', year: 'numeric',
    hour: '2-digit', minute: '2-digit'
  });
}

// ==================== LISTA ====================
function ServiceOrdersList() {
  const [orders, setOrders] = useState([]);
  const [clientes, setClientes] = useState([]);
  const [dashboard, setDashboard] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loadingClientes, setLoadingClientes] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [filterStatus, setFilterStatus] = useState("");
  const [filterCategoria, setFilterCategoria] = useState("");
  const [filterCliente, setFilterCliente] = useState("");
  const navigate = useNavigate();

  const fetchData = useCallback(async () => {
    try {
      const token = sessionStorage.getItem("luvcorp_token");
      const headers = { Authorization: `Bearer ${token}` };
      
      let url = `${API}/service-orders`;
      const params = new URLSearchParams();
      if (filterStatus) params.append("status", filterStatus);
      if (filterCategoria) params.append("categoria", filterCategoria);
      if (filterCliente) params.append("cliente_id", filterCliente);
      if (params.toString()) url += `?${params.toString()}`;
      
      const [ordersRes, clientesRes, dashboardRes] = await Promise.all([
        axios.get(url, { headers, timeout: 15000 }),
        axios.get(`${API}/clientes-fornecedores?tipo=cliente`, { headers, timeout: 15000 }),
        axios.get(`${API}/service-orders/dashboard/resumo`, { headers, timeout: 15000 })
      ]);
      
      setOrders(ordersRes.data);
      setClientes(clientesRes.data);
      setLoadingClientes(false);
      setDashboard(dashboardRes.data);
    } catch (error) {
      toast.error("Erro ao carregar ordens de serviço");
      setLoadingClientes(false);
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filterStatus, filterCategoria, filterCliente]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const filteredOrders = orders.filter(order =>
    !searchTerm ||
    order.titulo?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    order.numero?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    order.setor?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    order.cliente_nome?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const handleDelete = async (id) => {
    if (!confirm("Excluir esta OS?")) return;
    try {
      const token = sessionStorage.getItem("luvcorp_token");
      await axios.delete(`${API}/service-orders/${id}`, { headers: { Authorization: `Bearer ${token}` } });
      toast.success("OS excluída");
      fetchData();
    } catch (err) {
      toast.error(extractErrorMessage(err, "Erro ao excluir"));
    }
  };

  if (loading) return <MainLayout><div className="p-8 text-center">Carregando...</div></MainLayout>;

  return (
    <MainLayout>
      <div className="space-y-6" data-testid="service-orders-list">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-heading font-bold text-luvcorp-navy">Ordens de Serviço</h1>
            <p className="text-slate-500">Gestão completa de OS - Enterprise Level</p>
          </div>
          <Button onClick={() => navigate("/service-orders/nova")} className="bg-luvcorp-gold hover:bg-luvcorp-gold-dark text-white">
            <Plus className="h-4 w-4 mr-2" />
            Nova OS
          </Button>
        </div>

        {/* Dashboard KPIs */}
        {dashboard && (
          <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
            <Card className="border-0 shadow-sm">
              <CardContent className="p-4">
                <p className="text-sm text-slate-500">Total de OS</p>
                <p className="text-2xl font-bold text-luvcorp-navy">{dashboard.total}</p>
              </CardContent>
            </Card>
            <Card className="border-0 shadow-sm">
              <CardContent className="p-4">
                <p className="text-sm text-slate-500">Abertas Hoje</p>
                <p className="text-2xl font-bold text-blue-600">{dashboard.abertas_hoje}</p>
              </CardContent>
            </Card>
            <Card className="border-0 shadow-sm border-l-4 border-l-amber-500">
              <CardContent className="p-4">
                <p className="text-sm text-slate-500">Em Execução</p>
                <p className="text-2xl font-bold text-amber-600">{dashboard.por_status?.em_execucao || 0}</p>
              </CardContent>
            </Card>
            <Card className="border-0 shadow-sm border-l-4 border-l-red-500">
              <CardContent className="p-4">
                <p className="text-sm text-slate-500">Atrasadas</p>
                <p className="text-2xl font-bold text-red-600">{dashboard.atrasadas}</p>
              </CardContent>
            </Card>
            <Card className="border-0 shadow-sm border-l-4 border-l-orange-500">
              <CardContent className="p-4">
                <p className="text-sm text-slate-500">Com Pendências</p>
                <p className="text-2xl font-bold text-orange-600">{dashboard.com_pendencias}</p>
              </CardContent>
            </Card>
          </div>
        )}

        {/* Filters */}
        <Card className="border-0 shadow-sm">
          <CardContent className="p-4">
            <div className="flex flex-col md:flex-row gap-3">
              <div className="relative flex-1">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-slate-400" />
                <Input
                  data-testid="os-search"
                  placeholder="Buscar por número, título, setor..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-10"
                />
              </div>
              <div className="grid grid-cols-3 gap-2 md:flex md:gap-3">
              <NativeSelect
                data-testid="os-filter-status"
                value={filterStatus}
                onValueChange={setFilterStatus}
                options={[
                  { value: "", label: "Todos Status" },
                  ...activeStatusOptions
                ]}
                className="md:w-[180px]"
              />
              <NativeSelect
                data-testid="os-filter-categoria"
                value={filterCategoria}
                onValueChange={setFilterCategoria}
                options={[
                  { value: "", label: "Todas Categorias" },
                  ...categorias.map(c => ({ value: c.value, label: c.label }))
                ]}
                className="md:w-[180px]"
              />
              <NativeSelect
                data-testid="os-filter-cliente"
                value={filterCliente}
                onValueChange={setFilterCliente}
                loading={loadingClientes}
                options={[
                  { value: "", label: "Todos Clientes" },
                  ...clientes.map(c => ({ value: c.id, label: c.nome }))
                ]}
                className="md:w-[200px]"
              />
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Mobile Cards / Desktop Table */}
        <Card className="border-0 shadow-sm">
          <CardContent className="p-0">
            {/* Desktop Table */}
            <div className="hidden md:block">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Número</TableHead>
                    <TableHead>Título / Setor</TableHead>
                    <TableHead>Cliente</TableHead>
                    <TableHead>Tipo</TableHead>
                    <TableHead>Prioridade</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Abertura</TableHead>
                    <TableHead className="text-right">Ações</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredOrders.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={8} className="text-center py-8 text-slate-500">
                        Nenhuma OS encontrada
                      </TableCell>
                    </TableRow>
                  ) : (
                    filteredOrders.map((order) => {
                      const status = statusConfig[order.status] || statusConfig.enviado;
                      const prioridade = prioridades.find(p => p.value === order.prioridade);
                      const StatusIcon = status.icon;
                      return (
                        <TableRow key={order.id} className="cursor-pointer hover:bg-slate-50" onClick={() => navigate(`/service-orders/${order.id}`)}>
                          <TableCell className="font-mono font-bold text-luvcorp-navy">{order.numero}</TableCell>
                          <TableCell>
                            <div>
                              <p className="font-medium">{order.titulo}</p>
                              <p className="text-xs text-slate-500 flex items-center gap-1">
                                <MapPin className="h-3 w-3" />{order.setor}
                              </p>
                            </div>
                          </TableCell>
                          <TableCell>{order.cliente_nome || "-"}</TableCell>
                          <TableCell>
                            <Badge variant="outline">{order.tipo === "manutencao" ? "Manutenção" : "Reparo"}</Badge>
                          </TableCell>
                          <TableCell>
                            <Badge className={prioridade?.class || ""}>{prioridade?.label || order.prioridade}</Badge>
                          </TableCell>
                          <TableCell>
                            <div className="flex items-center gap-1">
                              <StatusIcon className="h-4 w-4" />
                              <Badge className={status.class}>{status.label}</Badge>
                            </div>
                          </TableCell>
                          <TableCell>{formatDate(order.data_abertura)}</TableCell>
                          <TableCell className="text-right">
                            <div className="flex justify-end gap-1" onClick={(e) => e.stopPropagation()}>
                              <Button variant="ghost" size="sm" onClick={() => navigate(`/service-orders/${order.id}`)}>
                                <Eye className="h-4 w-4" />
                              </Button>
                              <Button variant="ghost" size="sm" onClick={() => navigate(`/service-orders/${order.id}/editar`)}>
                                <Edit className="h-4 w-4" />
                              </Button>
                              <Button variant="ghost" size="sm" onClick={() => handleDelete(order.id)}>
                                <Trash2 className="h-4 w-4 text-red-500" />
                              </Button>
                            </div>
                          </TableCell>
                        </TableRow>
                      );
                    })
                  )}
                </TableBody>
              </Table>
            </div>

            {/* Mobile Cards */}
            <div className="md:hidden divide-y">
              {filteredOrders.length === 0 ? (
                <div className="text-center py-10 text-slate-500">Nenhuma OS encontrada</div>
              ) : (
                filteredOrders.map((order) => {
                  const status = statusConfig[order.status] || statusConfig.enviado;
                  const prioridade = prioridades.find(p => p.value === order.prioridade);
                  const StatusIcon = status.icon;
                  return (
                    <div key={order.id} className="p-4 active:bg-slate-50 transition-colors" onClick={() => navigate(`/service-orders/${order.id}`)}>
                      <div className="flex items-start justify-between gap-3">
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 mb-1">
                            <span className="font-mono text-sm font-bold text-luvcorp-navy">{order.numero}</span>
                            <Badge className={`${status.class} text-xs px-1.5 py-0`}>
                              <StatusIcon className="h-3 w-3 mr-1" />{status.label}
                            </Badge>
                          </div>
                          <p className="font-medium text-sm truncate">{order.titulo}</p>
                          <div className="flex items-center gap-3 mt-1.5 text-xs text-slate-500">
                            <span className="flex items-center gap-1"><MapPin className="h-3 w-3" />{order.setor}</span>
                            <span className="flex items-center gap-1"><Calendar className="h-3 w-3" />{formatDate(order.data_abertura)}</span>
                          </div>
                          <div className="flex items-center gap-2 mt-2">
                            <Badge variant="outline" className="text-xs">{order.tipo === "manutencao" ? "Manutenção" : "Reparo"}</Badge>
                            <Badge className={`${prioridade?.class || ""} text-xs`}>{prioridade?.label || order.prioridade}</Badge>
                          </div>
                        </div>
                        <div className="flex flex-col gap-1" onClick={(e) => e.stopPropagation()}>
                          <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => navigate(`/service-orders/${order.id}`)}>
                            <Eye className="h-4 w-4" />
                          </Button>
                          <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => navigate(`/service-orders/${order.id}/editar`)}>
                            <Edit className="h-4 w-4" />
                          </Button>
                        </div>
                      </div>
                    </div>
                  );
                })
              )}
            </div>
          </CardContent>
        </Card>
      </div>
    </MainLayout>
  );
}

// ==================== FORMULÁRIO ====================
function ServiceOrderForm() {
  const { id } = useParams();
  const navigate = useNavigate();
  const isEdit = !!id;
  
  const [loading, setLoading] = useState(true);
  const [loadingDropdowns, setLoadingDropdowns] = useState(true);
  const [saving, setSaving] = useState(false);
  const [clientes, setClientes] = useState([]);
  const [setores, setSetores] = useState([]);
  const [funcionarios, setFuncionarios] = useState([]);
  const [tiposCustom, setTiposCustom] = useState([]);
  const [categoriasCustom, setCategoriasCustom] = useState([]);
  const [fotoPreview, setFotoPreview] = useState(null);
  const [fetchError, setFetchError] = useState(null);
  const retryCountRef = useRef(0);
  
  const [formData, setFormData] = useState({
    cliente_id: "",
    solicitante_nome: "",
    solicitante_contato: "",
    tipo: "manutencao",
    categoria: "manutencao_predial",
    prioridade: "media",
    setor: "",
    localizacao_complemento: "",
    titulo: "",
    descricao: "",
    previsao_inicio: "",
    previsao_termino: "",
    profissionais_ids: [],
    foto_inicial: null,
    notificar_whatsapp: true,
    notificar_email: true
  });

  const fetchDropdowns = useCallback(async () => {
    const token = sessionStorage.getItem("luvcorp_token");
    const headers = { Authorization: `Bearer ${token}` };
    
    // Try cache first for faster mobile load
    const cachedClientes = sessionStorage.getItem("os_cache_clientes");
    const cachedSetores = sessionStorage.getItem("os_cache_setores");
    const cachedFuncionarios = sessionStorage.getItem("os_cache_funcionarios");
    if (cachedClientes && cachedSetores && cachedFuncionarios) {
      setClientes(JSON.parse(cachedClientes));
      setSetores(JSON.parse(cachedSetores));
      setFuncionarios(JSON.parse(cachedFuncionarios));
      setLoadingDropdowns(false);
    }

    try {
      const [clientesRes, setoresRes, funcRes] = await Promise.all([
        axios.get(`${API}/clientes-fornecedores?tipo=cliente`, { headers, timeout: 15000 }),
        axios.get(`${API}/service-orders/setores`, { headers, timeout: 15000 }),
        axios.get(`${API}/funcionarios`, { headers, timeout: 15000 })
      ]);
      
      setClientes(clientesRes.data);
      setSetores(setoresRes.data);
      setFuncionarios(funcRes.data);
      sessionStorage.setItem("os_cache_clientes", JSON.stringify(clientesRes.data));
      sessionStorage.setItem("os_cache_setores", JSON.stringify(setoresRes.data));
      sessionStorage.setItem("os_cache_funcionarios", JSON.stringify(funcRes.data));
      setLoadingDropdowns(false);
      setFetchError(null);
      retryCountRef.current = 0;
    } catch (err) {
      if (!cachedClientes && retryCountRef.current < 3) {
        retryCountRef.current += 1;
        setTimeout(() => fetchDropdowns(), 2000 * retryCountRef.current);
        return;
      }
      if (!cachedClientes) {
        setFetchError("Erro ao carregar listas. Verifique sua conexão.");
        setLoadingDropdowns(false);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const fetchData = async () => {
      await fetchDropdowns();
      
      if (isEdit) {
        try {
          const token = sessionStorage.getItem("luvcorp_token");
          const headers = { Authorization: `Bearer ${token}` };
          const osRes = await axios.get(`${API}/service-orders/${id}`, { headers, timeout: 15000 });
          const os = osRes.data;
          // Ensure custom values (not in predefined lists) are added as options
          if (os.tipo && !tipos.find(t => t.value === os.tipo)) {
            setTiposCustom(prev => prev.includes(os.tipo) ? prev : [...prev, os.tipo]);
          }
          if (os.categoria && !categorias.find(c => c.value === os.categoria)) {
            setCategoriasCustom(prev => prev.includes(os.categoria) ? prev : [...prev, os.categoria]);
          }
          if (os.setor && !setores.includes(os.setor)) {
            setSetores(prev => prev.includes(os.setor) ? prev : [...prev, os.setor]);
          }
          setFormData({
            cliente_id: os.cliente_id || "",
            solicitante_nome: os.solicitante_nome || "",
            solicitante_contato: os.solicitante_contato || "",
            tipo: os.tipo || "manutencao",
            categoria: os.categoria || "manutencao_predial",
            prioridade: os.prioridade || "media",
            setor: os.setor || "",
            localizacao_complemento: os.localizacao_complemento || "",
            titulo: os.titulo || "",
            descricao: os.descricao || "",
            previsao_inicio: os.previsao_inicio || "",
            previsao_termino: os.previsao_termino || "",
            profissionais_ids: os.profissionais?.map(p => p.id) || [],
            foto_inicial: os.foto_inicial,
            notificar_whatsapp: os.notificar_whatsapp ?? true,
            notificar_email: os.notificar_email ?? true
          });
          if (os.foto_inicial) setFotoPreview(os.foto_inicial);
        } catch (err) {
          toast.error("Erro ao carregar dados da OS");
        }
      }
      setLoading(false);
    };
    fetchData();
  }, [id, isEdit, fetchDropdowns]);

  const handleFotoChange = (e) => {
    const file = e.target.files?.[0];
    if (file) {
      const reader = new FileReader();
      reader.onloadend = () => {
        const base64 = reader.result;
        setFormData({ ...formData, foto_inicial: base64 });
        setFotoPreview(base64);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.cliente_id || !formData.solicitante_nome || !formData.setor || !formData.titulo || !formData.descricao) {
      toast.error("Preencha todos os campos obrigatórios");
      return;
    }

    setSaving(true);
    try {
      const token = sessionStorage.getItem("luvcorp_token");
      const headers = { Authorization: `Bearer ${token}` };
      
      if (isEdit) {
        await axios.put(`${API}/service-orders/${id}`, formData, { headers });
        toast.success("OS atualizada com sucesso");
      } else {
        await axios.post(`${API}/service-orders`, formData, { headers });
        toast.success("OS criada com sucesso");
      }
      navigate("/service-orders");
    } catch (err) {
      toast.error(extractErrorMessage(err, "Erro ao salvar"));
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <MainLayout><div className="p-8 text-center">Carregando...</div></MainLayout>;

  return (
    <MainLayout>
      <div className="space-y-6" data-testid="service-order-form">
        <div className="flex items-center gap-4">
          <Link to="/service-orders">
            <Button variant="ghost" size="sm"><ArrowLeft className="h-4 w-4 mr-2" />Voltar</Button>
          </Link>
          <div>
            <h1 className="text-2xl font-heading font-bold text-luvcorp-navy">
              {isEdit ? "Editar OS" : "Nova Ordem de Serviço"}
            </h1>
            <p className="text-slate-500">Preencha os dados da OS</p>
          </div>
        </div>

        {fetchError && (
          <Card className="border-red-200 bg-red-50">
            <CardContent className="p-4 flex items-center justify-between">
              <p className="text-sm text-red-700">{fetchError}</p>
              <Button variant="outline" size="sm" onClick={() => { setFetchError(null); setLoadingDropdowns(true); retryCountRef.current = 0; fetchDropdowns(); }}>
                Tentar Novamente
              </Button>
            </CardContent>
          </Card>
        )}

        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Identificação */}
          <Card className="border-0 shadow-sm">
            <CardHeader><CardTitle className="text-lg">Identificação</CardTitle></CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Cliente (Condomínio) *</Label>
                  <SearchablePicker
                    testId="os-form-cliente"
                    value={formData.cliente_id}
                    onChange={(v) => setFormData({...formData, cliente_id: v})}
                    placeholder={loadingDropdowns ? "Carregando..." : "Buscar cliente por nome..."}
                    emptyText="Nenhum cliente encontrado"
                    options={clientes.map(c => ({ value: c.id, label: c.nome, sublabel: c.documento || "" }))}
                  />
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Solicitante *</Label>
                    <Input
                      data-testid="os-form-solicitante"
                      value={formData.solicitante_nome}
                      onChange={(e) => setFormData({...formData, solicitante_nome: e.target.value})}
                      placeholder="Nome do solicitante"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Contato</Label>
                    <Input
                      data-testid="os-form-contato"
                      value={formData.solicitante_contato}
                      onChange={(e) => setFormData({...formData, solicitante_contato: e.target.value})}
                      placeholder="Telefone/Email"
                    />
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Classificação */}
          <Card className="border-0 shadow-sm">
            <CardHeader><CardTitle className="text-lg">Classificação</CardTitle></CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div className="space-y-2">
                  <Label>Tipo *</Label>
                  <SearchablePicker
                    testId="os-form-tipo"
                    value={formData.tipo}
                    onChange={(v) => setFormData({...formData, tipo: v})}
                    placeholder="Selecione ou digite..."
                    emptyText="Nenhum tipo encontrado"
                    options={[
                      ...tipos,
                      ...tiposCustom.map(t => ({ value: t, label: t }))
                    ]}
                    createNewLabel="Adicionar novo tipo"
                    onCreateNew={(q) => {
                      const novo = (q || "").trim();
                      if (!novo) { toast.info("Digite o nome do novo tipo no campo de busca"); return; }
                      if (!tiposCustom.includes(novo)) setTiposCustom(prev => [...prev, novo]);
                      setFormData(fd => ({...fd, tipo: novo}));
                      toast.success(`Tipo "${novo}" adicionado`);
                    }}
                  />
                </div>
                <div className="space-y-2">
                  <Label>Categoria *</Label>
                  <SearchablePicker
                    testId="os-form-categoria"
                    value={formData.categoria}
                    onChange={(v) => setFormData({...formData, categoria: v})}
                    placeholder="Selecione ou digite..."
                    emptyText="Nenhuma categoria encontrada"
                    options={[
                      ...categorias.map(c => ({ value: c.value, label: c.label })),
                      ...categoriasCustom.map(c => ({ value: c, label: c }))
                    ]}
                    createNewLabel="Adicionar nova categoria"
                    onCreateNew={(q) => {
                      const novo = (q || "").trim();
                      if (!novo) { toast.info("Digite o nome da nova categoria no campo de busca"); return; }
                      if (!categoriasCustom.includes(novo)) setCategoriasCustom(prev => [...prev, novo]);
                      setFormData(fd => ({...fd, categoria: novo}));
                      toast.success(`Categoria "${novo}" adicionada`);
                    }}
                  />
                </div>
                <div className="space-y-2">
                  <Label>Prioridade *</Label>
                  <NativeSelect
                    data-testid="os-form-prioridade"
                    value={formData.prioridade}
                    onValueChange={(v) => setFormData({...formData, prioridade: v})}
                    options={prioridades.map(p => ({ value: p.value, label: p.label }))}
                  />
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Localização */}
          <Card className="border-0 shadow-sm">
            <CardHeader><CardTitle className="text-lg">Localização</CardTitle></CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Setor *</Label>
                  <SearchablePicker
                    testId="os-form-setor"
                    value={formData.setor}
                    onChange={(v) => setFormData({...formData, setor: v})}
                    placeholder={loadingDropdowns ? "Carregando..." : "Selecione ou digite..."}
                    emptyText="Nenhum setor encontrado"
                    options={setores.map(s => ({ value: s, label: s }))}
                    createNewLabel="Adicionar novo local"
                    onCreateNew={(q) => {
                      const novo = (q || "").trim();
                      if (!novo) { toast.info("Digite o nome do novo local no campo de busca"); return; }
                      if (!setores.includes(novo)) setSetores(prev => [...prev, novo]);
                      setFormData(fd => ({...fd, setor: novo}));
                      toast.success(`Local "${novo}" adicionado`);
                    }}
                  />
                </div>
                <div className="space-y-2">
                  <Label>Complemento da Localização</Label>
                  <Input
                    data-testid="os-form-complemento"
                    value={formData.localizacao_complemento}
                    onChange={(e) => setFormData({...formData, localizacao_complemento: e.target.value})}
                    placeholder="Ex: Próximo ao palco, 2º andar"
                  />
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Descrição */}
          <Card className="border-0 shadow-sm">
            <CardHeader><CardTitle className="text-lg">Descrição do Serviço</CardTitle></CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-2">
                <Label>Título *</Label>
                <Input
                  value={formData.titulo}
                  onChange={(e) => setFormData({...formData, titulo: e.target.value})}
                  placeholder="Título resumido da OS"
                />
              </div>
              <div className="space-y-2">
                <Label>Descrição Detalhada *</Label>
                <Textarea
                  value={formData.descricao}
                  onChange={(e) => setFormData({...formData, descricao: e.target.value})}
                  placeholder="Descreva detalhadamente o serviço necessário"
                  rows={4}
                />
              </div>
            </CardContent>
          </Card>

          {/* Previsão e Profissionais */}
          <Card className="border-0 shadow-sm">
            <CardHeader><CardTitle className="text-lg">Previsão e Equipe</CardTitle></CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Previsão de Início</Label>
                  <Input
                    data-testid="os-form-previsao-inicio"
                    type="date"
                    value={formData.previsao_inicio}
                    onChange={(e) => setFormData({...formData, previsao_inicio: e.target.value})}
                  />
                </div>
                <div className="space-y-2">
                  <Label>Previsão de Término</Label>
                  <Input
                    data-testid="os-form-previsao-termino"
                    type="date"
                    value={formData.previsao_termino}
                    onChange={(e) => setFormData({...formData, previsao_termino: e.target.value})}
                  />
                </div>
              </div>
              <div className="space-y-2">
                <Label className="flex items-center gap-2">
                  <Users className="h-4 w-4" /> Profissionais Responsáveis
                </Label>
                <MultiSelect
                  data-testid="os-form-profissionais"
                  values={formData.profissionais_ids}
                  onValuesChange={(v) => setFormData({...formData, profissionais_ids: v})}
                  placeholder="Selecione os profissionais..."
                  loading={loadingDropdowns}
                  options={funcionarios.map(f => ({ value: f.id, label: f.nome }))}
                />
              </div>
            </CardContent>
          </Card>

          {/* Foto Inicial */}
          <Card className="border-0 shadow-sm">
            <CardHeader><CardTitle className="text-lg">Foto Inicial</CardTitle></CardHeader>
            <CardContent className="space-y-4">
              <div className="flex items-center gap-4">
                <div className="flex-1">
                  <Label className="cursor-pointer">
                    <div className="flex items-center gap-2 p-4 border-2 border-dashed border-slate-200 rounded-lg hover:border-luvcorp-gold transition-colors">
                      <Camera className="h-6 w-6 text-slate-400" />
                      <span className="text-slate-500">Clique para adicionar foto</span>
                    </div>
                    <input type="file" accept="image/*" className="hidden" onChange={handleFotoChange} capture="environment" />
                  </Label>
                </div>
                {fotoPreview && (
                  <div className="w-32 h-32 border rounded-lg overflow-hidden">
                    <img src={fotoPreview} alt="Preview" className="w-full h-full object-cover" />
                  </div>
                )}
              </div>
            </CardContent>
          </Card>

          {/* Notificações */}
          <Card className="border-0 shadow-sm">
            <CardHeader><CardTitle className="text-lg">Notificações</CardTitle></CardHeader>
            <CardContent>
              <div className="flex gap-6">
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={formData.notificar_whatsapp}
                    onChange={(e) => setFormData({...formData, notificar_whatsapp: e.target.checked})}
                    className="rounded"
                  />
                  <MessageCircle className="h-4 w-4 text-green-500" />
                  <span className="text-sm">WhatsApp</span>
                </label>
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={formData.notificar_email}
                    onChange={(e) => setFormData({...formData, notificar_email: e.target.checked})}
                    className="rounded"
                  />
                  <Mail className="h-4 w-4 text-blue-500" />
                  <span className="text-sm">Email</span>
                </label>
              </div>
            </CardContent>
          </Card>

          <div className="flex justify-end gap-4">
            <Button type="button" variant="outline" onClick={() => navigate("/service-orders")}>Cancelar</Button>
            <Button type="submit" disabled={saving} className="bg-luvcorp-navy hover:bg-luvcorp-navy-dark">
              {saving ? "Salvando..." : isEdit ? "Salvar Alterações" : "Criar OS"}
            </Button>
          </div>
        </form>
      </div>
    </MainLayout>
  );
}

// ==================== VISUALIZAÇÃO ====================
function ServiceOrderView() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  
  const [order, setOrder] = useState(null);
  const [funcionarios, setFuncionarios] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showStatusDialog, setShowStatusDialog] = useState(false);
  const [showSignatureDialog, setShowSignatureDialog] = useState(false);
  const [signatureType, setSignatureType] = useState("profissional");

  const fetchOrder = useCallback(async () => {
    try {
      const token = sessionStorage.getItem("luvcorp_token");
      const headers = { Authorization: `Bearer ${token}` };
      
      const [orderRes, funcRes] = await Promise.all([
        axios.get(`${API}/service-orders/${id}`, { headers }),
        axios.get(`${API}/funcionarios`, { headers })
      ]);
      
      setOrder(orderRes.data);
      setFuncionarios(funcRes.data);
    } catch (err) {
      toast.error("Erro ao carregar OS");
      navigate("/service-orders");
    } finally {
      setLoading(false);
    }
  }, [id, navigate]);

  useEffect(() => { fetchOrder(); }, [fetchOrder]);

  const handleGerarPDF = async () => {
    try {
      toast.info("Gerando PDF...");
      const token = sessionStorage.getItem("luvcorp_token");
      const response = await axios.get(`${API}/service-orders/${id}/pdf`, {
        headers: { Authorization: `Bearer ${token}` },
        responseType: 'blob'
      });
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const fileName = `OS_${order?.numero || id}.pdf`;
      
      // Tentar compartilhamento nativo (mobile e desktop com suporte)
      if (navigator.share && navigator.canShare) {
        const file = new File([blob], fileName, { type: 'application/pdf' });
        const shareData = {
          title: `OS ${order?.numero} - ${order?.titulo}`,
          text: `Prezado cliente,\n\nSegue anexo, ordem de serviço contendo todo histórico.\nServiço concluído com êxito\n\nEquipe Obra Padrão · Sistema de Gestão de Obras`,
          files: [file]
        };
        if (navigator.canShare(shareData)) {
          await navigator.share(shareData);
          toast.success("PDF compartilhado!");
          return;
        }
      }
      
      // Fallback: download direto
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', fileName);
      document.body.appendChild(link);
      link.click();
      link.remove();
      setTimeout(() => window.URL.revokeObjectURL(url), 3000);
      toast.success("PDF baixado!");
    } catch (err) {
      if (err.name !== 'AbortError') {
        toast.error("Erro ao gerar PDF");
      }
    }
  };

  if (loading) return <MainLayout><div className="p-8 text-center">Carregando...</div></MainLayout>;
  if (!order) return null;

  const status = statusConfig[order.status] || statusConfig.enviado;
  const prioridade = prioridades.find(p => p.value === order.prioridade);
  const StatusIcon = status.icon;

  return (
    <MainLayout>
      <div className="space-y-4 md:space-y-6 print:space-y-4" data-testid="service-order-view">
        {/* Header - Mobile Responsive */}
        <div className="print:hidden">
          <div className="flex items-center gap-3 mb-3">
            <Link to="/service-orders">
              <Button variant="ghost" size="sm"><ArrowLeft className="h-4 w-4 mr-1" />Voltar</Button>
            </Link>
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 flex-wrap">
                <h1 className="text-xl md:text-2xl font-heading font-bold text-luvcorp-navy">{order.numero}</h1>
                <Badge className={prioridade?.class}>{prioridade?.label}</Badge>
              </div>
              <p className="text-sm text-slate-500 truncate">{order.titulo}</p>
            </div>
          </div>
          {/* Action Buttons - Mobile: stacked, Desktop: row */}
          <div className="flex gap-2">
            <Button variant="outline" size="sm" onClick={handleGerarPDF} className="text-xs md:text-sm" data-testid="btn-pdf">
              <Share2 className="h-4 w-4 mr-1" />PDF / Compartilhar
            </Button>
            <Button size="sm" onClick={() => setShowStatusDialog(true)} className="bg-luvcorp-gold hover:bg-luvcorp-gold-dark text-white text-xs md:text-sm" data-testid="btn-atualizar-status">
              Nova Atualização
            </Button>
          </div>
        </div>

        {/* Status Banner */}
        <Card className={`border-0 shadow-sm border-l-4 ${status.class.includes('red') ? 'border-l-red-500' : status.class.includes('amber') ? 'border-l-amber-500' : status.class.includes('emerald') ? 'border-l-emerald-500' : 'border-l-blue-500'}`}>
          <CardContent className="p-3 md:p-4">
            <div className="flex flex-col md:flex-row md:items-center justify-between gap-2">
              <div className="flex items-center gap-3">
                <StatusIcon className="h-6 w-6 shrink-0" />
                <div>
                  <Badge className={`${status.class} text-base md:text-lg px-3 py-1`}>{status.label}</Badge>
                  {order.status === "atrasado" && order.motivo_atraso && (
                    <p className="text-sm text-red-600 mt-1">Motivo: {order.motivo_atraso}</p>
                  )}
                  {order.status === "pendencia" && order.descricao_pendencia && (
                    <p className="text-sm text-orange-600 mt-1">Pendência: {order.descricao_pendencia}</p>
                  )}
                  {order.status === "aditivado" && order.descricao_aditivo && (
                    <p className="text-sm text-purple-600 mt-1">Aditivo: {order.descricao_aditivo}</p>
                  )}
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Info Grid - Mobile Friendly */}
        <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
          <Card className="border-0 shadow-sm">
            <CardContent className="p-3 md:p-4">
              <p className="text-xs text-slate-400 mb-1">Cliente</p>
              <p className="font-medium text-sm">{order.cliente_nome}</p>
              <p className="text-xs text-slate-500 mt-1">Sol: {order.solicitante_nome}</p>
              {order.solicitante_contato && (
                <p className="text-xs text-slate-400">{order.solicitante_contato}</p>
              )}
            </CardContent>
          </Card>
          <Card className="border-0 shadow-sm">
            <CardContent className="p-3 md:p-4">
              <p className="text-xs text-slate-400 mb-1">Localização</p>
              <p className="font-medium text-sm flex items-start gap-1"><MapPin className="h-3.5 w-3.5 shrink-0 mt-0.5" />{order.setor}</p>
              {order.localizacao_complemento && (
                <p className="text-xs text-slate-400 ml-5">{order.localizacao_complemento}</p>
              )}
            </CardContent>
          </Card>
          <Card className="border-0 shadow-sm col-span-2 md:col-span-1">
            <CardContent className="p-3 md:p-4">
              <p className="text-xs text-slate-400 mb-1">Equipe</p>
              {order.profissionais && order.profissionais.length > 0 ? (
                <div className="space-y-1">
                  {order.profissionais.map((p, i) => (
                    <p key={i} className="font-medium text-sm flex items-center gap-1">
                      <User className="h-3 w-3 shrink-0" />{p.nome}
                    </p>
                  ))}
                </div>
              ) : (
                <p className="font-medium text-sm">{order.profissional_responsavel_nome || "Não atribuído"}</p>
              )}
              <div className="flex flex-wrap gap-x-4 gap-y-0 mt-1">
                {order.data_hora_inicio && (
                  <p className="text-xs text-slate-500">Início: {formatDateTime(order.data_hora_inicio)}</p>
                )}
                {order.data_hora_fim && (
                  <p className="text-xs text-slate-500">Fim: {formatDateTime(order.data_hora_fim)}</p>
                )}
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Previsão de Datas */}
        {(order.previsao_inicio || order.previsao_termino) && (
          <Card className="border-0 shadow-sm border-l-4 border-l-blue-400">
            <CardContent className="p-3 md:p-4">
              <div className="flex items-center gap-2 mb-2">
                <Calendar className="h-4 w-4 text-blue-500" />
                <p className="text-sm font-medium text-slate-700">Datas Previstas</p>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <p className="text-xs text-slate-400">Início Previsto</p>
                  <p className="text-sm font-medium">{formatDate(order.previsao_inicio)}</p>
                </div>
                <div>
                  <p className="text-xs text-slate-400">Término Previsto</p>
                  <p className="text-sm font-medium">{formatDate(order.previsao_termino)}</p>
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Descrição */}
        <Card className="border-0 shadow-sm">
          <CardHeader className="pb-2"><CardTitle className="text-base">Descrição</CardTitle></CardHeader>
          <CardContent>
            <p className="whitespace-pre-wrap text-sm">{order.descricao}</p>
          </CardContent>
        </Card>

        {/* Fotos - Mobile Grid */}
        {(order.foto_inicial || order.fotos_execucao?.length > 0 || order.foto_final) && (
          <Card className="border-0 shadow-sm">
            <CardHeader className="pb-2"><CardTitle className="text-base">Registro Fotográfico</CardTitle></CardHeader>
            <CardContent>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                {order.foto_inicial && (
                  <div className="space-y-1">
                    <p className="text-xs text-slate-500">Foto Inicial</p>
                    <img src={order.foto_inicial} alt="Inicial" className="w-full h-32 object-cover rounded-lg" />
                  </div>
                )}
                {order.fotos_execucao?.map((foto, i) => (
                  <div key={i} className="space-y-1">
                    <p className="text-xs text-slate-500">Execução {i + 1}</p>
                    <img src={foto} alt={`Execução ${i + 1}`} className="w-full h-32 object-cover rounded-lg" />
                  </div>
                ))}
                {order.foto_final && (
                  <div className="space-y-1">
                    <p className="text-xs text-slate-500">Foto Final</p>
                    <img src={order.foto_final} alt="Final" className="w-full h-32 object-cover rounded-lg" />
                  </div>
                )}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Assinaturas - Só aparece quando OS é concluída */}
        {(order.status === "concluido" || order.assinatura_profissional || order.assinatura_solicitante) && (
          <Card className="border-0 shadow-sm border-l-4 border-l-emerald-500">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <PenTool className="h-5 w-5" />
                Assinaturas de Conclusão
              </CardTitle>
              {order.status === "concluido" && (!order.assinatura_profissional || !order.assinatura_solicitante) && (
                <CardDescription className="text-amber-600 font-medium">
                  Assinaturas pendentes para validar a conclusão do serviço
                </CardDescription>
              )}
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                <div className="text-center space-y-3">
                  <p className="text-sm font-medium text-slate-600">Profissional Responsável</p>
                  {order.assinatura_profissional ? (
                    <div className="border rounded-lg p-3 bg-slate-50">
                      <img src={order.assinatura_profissional} alt="Assinatura Profissional" className="h-20 mx-auto" />
                    </div>
                  ) : (
                    <div className="h-24 border-2 border-dashed border-slate-300 rounded-lg flex items-center justify-center">
                      <span className="text-slate-400 text-sm">Não assinado</span>
                    </div>
                  )}
                  <p className="text-sm">{order.profissional_responsavel_nome || "-"}</p>
                  {order.status === "concluido" && !order.assinatura_profissional && (
                    <Button
                      data-testid="btn-assinar-profissional"
                      variant="outline"
                      className="w-full border-emerald-300 text-emerald-700 hover:bg-emerald-50"
                      onClick={() => { setSignatureType("profissional"); setShowSignatureDialog(true); }}
                    >
                      <PenTool className="h-4 w-4 mr-2" />Coletar Assinatura
                    </Button>
                  )}
                </div>
                <div className="text-center space-y-3">
                  <p className="text-sm font-medium text-slate-600">Solicitante</p>
                  {order.assinatura_solicitante ? (
                    <div className="border rounded-lg p-3 bg-slate-50">
                      <img src={order.assinatura_solicitante} alt="Assinatura Solicitante" className="h-20 mx-auto" />
                    </div>
                  ) : (
                    <div className="h-24 border-2 border-dashed border-slate-300 rounded-lg flex items-center justify-center">
                      <span className="text-slate-400 text-sm">Não assinado</span>
                    </div>
                  )}
                  <p className="text-sm">{order.solicitante_nome}</p>
                  {order.status === "concluido" && !order.assinatura_solicitante && (
                    <Button
                      data-testid="btn-assinar-solicitante"
                      variant="outline"
                      className="w-full border-emerald-300 text-emerald-700 hover:bg-emerald-50"
                      onClick={() => { setSignatureType("solicitante"); setShowSignatureDialog(true); }}
                    >
                      <PenTool className="h-4 w-4 mr-2" />Coletar Assinatura
                    </Button>
                  )}
                </div>
              </div>
              {order.assinatura_profissional && order.assinatura_solicitante && (
                <div className="mt-4 p-3 bg-emerald-50 rounded-lg text-center">
                  <p className="text-sm text-emerald-700 font-medium flex items-center justify-center gap-2">
                    <CheckCircle2 className="h-4 w-4" />
                    Serviço concluído e validado com ambas as assinaturas
                  </p>
                </div>
              )}
            </CardContent>
          </Card>
        )}

        {/* Histórico */}
        <Card className="border-0 shadow-sm">
          <CardHeader><CardTitle className="flex items-center gap-2"><History className="h-5 w-5" />Histórico de Atualizações</CardTitle></CardHeader>
          <CardContent>
            <div className="space-y-3">
              {(order.historico_status || []).slice().reverse().map((h, i) => {
                const st = statusConfig[h.status] || { label: h.status, class: "bg-slate-100" };
                const isUpdate = h.tipo === "atualizacao";
                return (
                  <div key={i} className="flex items-start gap-3 pb-3 border-b border-slate-100 last:border-0">
                    <div className={`p-1.5 rounded ${isUpdate ? 'bg-slate-100' : st.class}`}>
                      {isUpdate ? <Edit className="h-4 w-4" /> : <Clock className="h-4 w-4" />}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <Badge className={st.class}>{isUpdate ? "Atualização" : st.label}</Badge>
                        <span className="text-xs text-slate-500">{formatDateTime(h.data)}</span>
                      </div>
                      <p className="text-sm text-slate-500">{h.usuario_nome}</p>
                      {h.observacoes && (
                        <p className="text-sm text-slate-700 mt-1 bg-slate-50 rounded p-2">
                          <span className="text-xs text-slate-400 block mb-0.5">Atividade:</span>
                          {h.observacoes}
                        </p>
                      )}
                      {h.motivo_atraso && <p className="text-sm text-red-600 mt-1">Motivo: {h.motivo_atraso}</p>}
                      {h.descricao_pendencia && <p className="text-sm text-orange-600 mt-1">Pendência: {h.descricao_pendencia}</p>}
                      {h.descricao_aditivo && (
                        <p className="text-sm text-purple-600 mt-1">Aditivo: {h.descricao_aditivo}</p>
                      )}
                      {h.fotos && h.fotos.length > 0 && (
                        <div className="flex flex-wrap gap-1.5 mt-2">
                          {h.fotos.map((foto, fi) => (
                            <img key={fi} src={foto} alt={`Foto ${fi + 1}`} className="w-14 h-14 rounded object-cover border cursor-pointer hover:opacity-80" onClick={() => window.open(foto, '_blank')} />
                          ))}
                        </div>
                      )}
                    </div>
                  </div>
                );
              })}
              {(!order.historico_status || order.historico_status.length === 0) && (
                <p className="text-sm text-slate-400 text-center py-4">Nenhum registro no histórico</p>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Dialogs */}
        <StatusUpdateDialog 
          open={showStatusDialog}
          onClose={() => setShowStatusDialog(false)}
          order={order}
          funcionarios={funcionarios}
          onSaved={fetchOrder}
        />

        <SignatureDialog
          open={showSignatureDialog}
          onClose={() => setShowSignatureDialog(false)}
          type={signatureType}
          order={order}
          onSaved={fetchOrder}
        />
      </div>
    </MainLayout>
  );
}

// ==================== STATUS UPDATE DIALOG ====================
function StatusUpdateDialog({ open, onClose, order, funcionarios, onSaved }) {
  const [saving, setSaving] = useState(false);
  const [fotos, setFotos] = useState([]);
  const sigProfRef = useRef(null);
  const sigSolRef = useRef(null);
  const [isSigDrawing, setIsSigDrawing] = useState(false);
  const [formData, setFormData] = useState({
    status: "",
    observacoes_execucao: "",
    motivo_atraso: "",
    descricao_pendencia: "",
    descricao_aditivo: ""
  });

  // Reset form every time dialog opens - each update is a NEW entry
  useEffect(() => {
    if (open) {
      setFormData({
        status: "",
        observacoes_execucao: "",
        motivo_atraso: "",
        descricao_pendencia: "",
        descricao_aditivo: ""
      });
      setFotos([]);
    }
  }, [open]);

  // Initialize signature canvases when switching to concluido
  useEffect(() => {
    if (formData.status === "concluido") {
      setTimeout(() => {
        [sigProfRef, sigSolRef].forEach(ref => {
          if (ref.current) {
            const ctx = ref.current.getContext('2d');
            ctx.fillStyle = 'white';
            ctx.fillRect(0, 0, ref.current.width, ref.current.height);
          }
        });
      }, 100);
    }
  }, [formData.status]);

  const startSigDraw = (e, ref) => {
    e.preventDefault();
    setIsSigDrawing(true);
    const canvas = ref.current;
    const ctx = canvas.getContext('2d');
    const rect = canvas.getBoundingClientRect();
    const scaleX = canvas.width / rect.width;
    const scaleY = canvas.height / rect.height;
    const x = ((e.clientX || e.touches?.[0]?.clientX) - rect.left) * scaleX;
    const y = ((e.clientY || e.touches?.[0]?.clientY) - rect.top) * scaleY;
    ctx.beginPath();
    ctx.moveTo(x, y);
  };

  const sigDraw = (e, ref) => {
    if (!isSigDrawing) return;
    e.preventDefault();
    const canvas = ref.current;
    const ctx = canvas.getContext('2d');
    const rect = canvas.getBoundingClientRect();
    const scaleX = canvas.width / rect.width;
    const scaleY = canvas.height / rect.height;
    const x = ((e.clientX || e.touches?.[0]?.clientX) - rect.left) * scaleX;
    const y = ((e.clientY || e.touches?.[0]?.clientY) - rect.top) * scaleY;
    ctx.lineWidth = 2;
    ctx.lineCap = 'round';
    ctx.strokeStyle = '#0a2240';
    ctx.lineTo(x, y);
    ctx.stroke();
  };

  const stopSigDraw = () => setIsSigDrawing(false);

  const clearSigCanvas = (ref) => {
    if (ref.current) {
      const ctx = ref.current.getContext('2d');
      ctx.fillStyle = 'white';
      ctx.fillRect(0, 0, ref.current.width, ref.current.height);
    }
  };

  const handleAddFotos = (e) => {
    const files = Array.from(e.target.files);
    files.forEach(file => {
      const reader = new FileReader();
      reader.onload = (ev) => {
        setFotos(prev => [...prev, ev.target.result]);
      };
      reader.readAsDataURL(file);
    });
    e.target.value = "";
  };

  const handleRemoveFoto = (index) => {
    setFotos(prev => prev.filter((_, i) => i !== index));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.status) {
      toast.error("Selecione o status da atualização");
      return;
    }
    if (formData.status === "atrasado" && !formData.motivo_atraso) {
      toast.error("Preencha o motivo do atraso");
      return;
    }
    if (formData.status === "pendencia" && !formData.descricao_pendencia) {
      toast.error("Descreva a pendência");
      return;
    }

    setSaving(true);
    try {
      const token = sessionStorage.getItem("luvcorp_token");
      
      const payload = {
        status: formData.status,
        observacoes_execucao: formData.observacoes_execucao || null,
        motivo_atraso: formData.motivo_atraso || null,
        descricao_pendencia: formData.descricao_pendencia || null,
        descricao_aditivo: formData.descricao_aditivo || null,
        fotos_atualizacao: fotos.length > 0 ? fotos : null
      };
      
      // Capture inline signatures when completing
      if (formData.status === "concluido") {
        try {
          if (sigProfRef.current) payload.assinatura_profissional = sigProfRef.current.toDataURL('image/png');
        } catch (e) {
          console.warn('[OS] Falha ao capturar assinatura do profissional:', e?.message || e);
        }
        try {
          if (sigSolRef.current) payload.assinatura_solicitante = sigSolRef.current.toDataURL('image/png');
        } catch (e) {
          console.warn('[OS] Falha ao capturar assinatura do solicitante:', e?.message || e);
        }
      }
      
      // Remover campos null para reduzir payload
      Object.keys(payload).forEach(k => { if (payload[k] === null) delete payload[k]; });
      
      await axios.put(`${API}/service-orders/${order.id}`, payload, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast.success("Atualização registrada com sucesso");
      onSaved();
      onClose();
    } catch (err) {
      toast.error(extractErrorMessage(err, "Erro ao registrar atualização"));
    } finally {
      setSaving(false);
    }
  };

  const isClosing = formData.status === "concluido";

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle data-testid="dialog-title">Nova Atualização</DialogTitle>
          <DialogDescription>OS {order?.numero} - Registrar nova atualização de status</DialogDescription>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4">
          {/* Status */}
          <div className="space-y-2">
            <Label>Status *</Label>
            <NativeSelect
              data-testid="os-update-status"
              value={formData.status}
              onValueChange={(v) => setFormData({...formData, status: v})}
              placeholder="Selecione o status..."
              options={activeStatusOptions}
            />
          </div>

          {/* Campos para Atrasado */}
          {formData.status === "atrasado" && (
            <Card className="border-red-200 bg-red-50">
              <CardContent className="p-4 space-y-2">
                <Label className="text-red-700">Motivo do Atraso *</Label>
                <Textarea
                  data-testid="os-update-motivo-atraso"
                  value={formData.motivo_atraso}
                  onChange={(e) => setFormData({...formData, motivo_atraso: e.target.value})}
                  placeholder="Descreva o motivo do atraso"
                  rows={3}
                />
              </CardContent>
            </Card>
          )}

          {/* Campos para Pendência */}
          {formData.status === "pendencia" && (
            <Card className="border-orange-200 bg-orange-50">
              <CardContent className="p-4 space-y-2">
                <Label className="text-orange-700">Descrição da Pendência *</Label>
                <Textarea
                  data-testid="os-update-descricao-pendencia"
                  value={formData.descricao_pendencia}
                  onChange={(e) => setFormData({...formData, descricao_pendencia: e.target.value})}
                  placeholder="Descreva detalhadamente a pendência"
                  rows={3}
                />
              </CardContent>
            </Card>
          )}

          {/* Campos para Aditivado */}
          {formData.status === "aditivado" && (
            <Card className="border-purple-200 bg-purple-50">
              <CardContent className="p-4 space-y-2">
                <Label className="text-purple-700">Descrição do Aditivo</Label>
                <Textarea
                  data-testid="os-update-descricao-aditivo"
                  value={formData.descricao_aditivo}
                  onChange={(e) => setFormData({...formData, descricao_aditivo: e.target.value})}
                  placeholder="Descreva os serviços aditivados ao orçamento"
                  rows={3}
                />
              </CardContent>
            </Card>
          )}

          {/* Fotos da Atualização */}
          <div className="space-y-2">
            <Label className="flex items-center gap-2">
              <Camera className="h-4 w-4" /> Fotos desta Atualização
            </Label>
            <div className="flex flex-wrap gap-2">
              {fotos.map((foto, i) => (
                <div key={i} className="relative w-16 h-16 rounded border overflow-hidden group">
                  <img src={foto} alt={`Foto ${i + 1}`} className="w-full h-full object-cover" />
                  <button
                    type="button"
                    onClick={() => handleRemoveFoto(i)}
                    className="absolute inset-0 bg-black/50 text-white opacity-0 group-hover:opacity-100 flex items-center justify-center transition-opacity"
                  >
                    <X className="h-4 w-4" />
                  </button>
                </div>
              ))}
              <label className="w-16 h-16 rounded border-2 border-dashed border-slate-300 flex items-center justify-center cursor-pointer hover:border-slate-400 transition-colors" data-testid="os-update-add-foto">
                <Camera className="h-5 w-5 text-slate-400" />
                <input
                  type="file"
                  accept="image/*"
                  multiple
                  capture="environment"
                  onChange={handleAddFotos}
                  className="hidden"
                />
              </label>
            </div>
            {fotos.length > 0 && <p className="text-xs text-slate-500">{fotos.length} foto(s) selecionada(s)</p>}
          </div>

          {/* Descrição da Atividade (sempre visível) */}
          <div className="space-y-2">
            <Label>Descrição da Atividade</Label>
            <Textarea
              data-testid="os-update-observacoes"
              value={formData.observacoes_execucao}
              onChange={(e) => setFormData({...formData, observacoes_execucao: e.target.value})}
              placeholder="Descreva a atividade realizada nesta atualização"
              rows={2}
            />
          </div>

          {/* Conclusão com Assinaturas Inline */}
          {isClosing && (
            <Card className="border-emerald-200 bg-emerald-50">
              <CardContent className="p-4 space-y-4">
                <p className="text-sm font-medium text-emerald-700">Conclusão da OS - Assinaturas</p>
                
                {/* Assinatura Profissional */}
                <div className="space-y-2 pt-2 border-t border-emerald-200">
                  <Label className="flex items-center gap-2">
                    <PenTool className="h-4 w-4" /> Assinatura do Profissional *
                  </Label>
                  <div className="bg-white border-2 border-emerald-200 rounded-lg overflow-hidden">
                    <canvas
                      ref={sigProfRef}
                      width={500}
                      height={150}
                      className="w-full touch-none cursor-crosshair"
                      style={{ height: "120px" }}
                      onMouseDown={(e) => startSigDraw(e, sigProfRef)}
                      onMouseMove={(e) => sigDraw(e, sigProfRef)}
                      onMouseUp={stopSigDraw}
                      onMouseLeave={stopSigDraw}
                      onTouchStart={(e) => startSigDraw(e, sigProfRef)}
                      onTouchMove={(e) => sigDraw(e, sigProfRef)}
                      onTouchEnd={stopSigDraw}
                    />
                  </div>
                  <Button type="button" variant="ghost" size="sm" className="text-xs text-slate-500"
                    onClick={() => clearSigCanvas(sigProfRef)}>
                    Limpar
                  </Button>
                </div>
                
                {/* Assinatura Solicitante */}
                <div className="space-y-2 pt-2 border-t border-emerald-200">
                  <Label className="flex items-center gap-2">
                    <PenTool className="h-4 w-4" /> Assinatura do Solicitante *
                  </Label>
                  <div className="bg-white border-2 border-emerald-200 rounded-lg overflow-hidden">
                    <canvas
                      ref={sigSolRef}
                      width={500}
                      height={150}
                      className="w-full touch-none cursor-crosshair"
                      style={{ height: "120px" }}
                      onMouseDown={(e) => startSigDraw(e, sigSolRef)}
                      onMouseMove={(e) => sigDraw(e, sigSolRef)}
                      onMouseUp={stopSigDraw}
                      onMouseLeave={stopSigDraw}
                      onTouchStart={(e) => startSigDraw(e, sigSolRef)}
                      onTouchMove={(e) => sigDraw(e, sigSolRef)}
                      onTouchEnd={stopSigDraw}
                    />
                  </div>
                  <Button type="button" variant="ghost" size="sm" className="text-xs text-slate-500"
                    onClick={() => clearSigCanvas(sigSolRef)}>
                    Limpar
                  </Button>
                </div>
              </CardContent>
            </Card>
          )}

          <DialogFooter className="gap-2">
            <Button type="button" variant="outline" onClick={onClose}>Cancelar</Button>
            <Button type="submit" disabled={saving || !formData.status} className="bg-luvcorp-navy hover:bg-luvcorp-navy-dark" data-testid="os-update-submit">
              {saving ? "Salvando..." : "Registrar Atualização"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

// ==================== SIGNATURE DIALOG (Simplified) ====================
function SignatureDialog({ open, onClose, type, order, onSaved }) {
  const canvasRef = useRef(null);
  const [isDrawing, setIsDrawing] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (open && canvasRef.current) {
      const canvas = canvasRef.current;
      const ctx = canvas.getContext('2d');
      ctx.fillStyle = 'white';
      ctx.fillRect(0, 0, canvas.width, canvas.height);
    }
  }, [open]);

  const startDrawing = (e) => {
    setIsDrawing(true);
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    const rect = canvas.getBoundingClientRect();
    const x = (e.clientX || e.touches?.[0]?.clientX) - rect.left;
    const y = (e.clientY || e.touches?.[0]?.clientY) - rect.top;
    ctx.beginPath();
    ctx.moveTo(x, y);
  };

  const draw = (e) => {
    if (!isDrawing) return;
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    const rect = canvas.getBoundingClientRect();
    const x = (e.clientX || e.touches?.[0]?.clientX) - rect.left;
    const y = (e.clientY || e.touches?.[0]?.clientY) - rect.top;
    ctx.lineWidth = 2;
    ctx.lineCap = 'round';
    ctx.strokeStyle = '#0a2240';
    ctx.lineTo(x, y);
    ctx.stroke();
  };

  const stopDrawing = () => setIsDrawing(false);

  const clearCanvas = () => {
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    ctx.fillStyle = 'white';
    ctx.fillRect(0, 0, canvas.width, canvas.height);
  };

  const saveSignature = async () => {
    const canvas = canvasRef.current;
    const signatureData = canvas.toDataURL('image/png');
    
    setSaving(true);
    try {
      const token = sessionStorage.getItem("luvcorp_token");
      const payload = type === "profissional" 
        ? { assinatura_profissional: signatureData }
        : { assinatura_solicitante: signatureData };
      
      await axios.put(`${API}/service-orders/${order.id}`, payload, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast.success("Assinatura salva");
      onSaved();
      onClose();
    } catch (err) {
      toast.error("Erro ao salvar assinatura");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>Assinatura {type === "profissional" ? "do Profissional" : "do Solicitante"}</DialogTitle>
        </DialogHeader>
        <div className="space-y-4">
          <div className="border-2 border-slate-200 rounded-lg overflow-hidden">
            <canvas
              ref={canvasRef}
              width={400}
              height={200}
              className="w-full touch-none cursor-crosshair"
              onMouseDown={startDrawing}
              onMouseMove={draw}
              onMouseUp={stopDrawing}
              onMouseLeave={stopDrawing}
              onTouchStart={startDrawing}
              onTouchMove={draw}
              onTouchEnd={stopDrawing}
            />
          </div>
          <div className="flex justify-between">
            <Button type="button" variant="outline" onClick={clearCanvas}>Limpar</Button>
            <div className="flex gap-2">
              <Button type="button" variant="outline" onClick={onClose}>Cancelar</Button>
              <Button onClick={saveSignature} disabled={saving} className="bg-luvcorp-navy hover:bg-luvcorp-navy-dark">
                {saving ? "Salvando..." : "Salvar Assinatura"}
              </Button>
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

// ==================== MAIN ====================
export default function ServiceOrdersPage() {
  return (
    <Routes>
      <Route index element={<ServiceOrdersList />} />
      <Route path="nova" element={<ServiceOrderForm />} />
      <Route path=":id" element={<ServiceOrderView />} />
      <Route path=":id/editar" element={<ServiceOrderForm />} />
    </Routes>
  );
}
